import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import Modal from './Modal';
import { showToast } from '../Utils/toast';
import config from '../Utils/config';
import logo from '../Assets/logo.png';
import { usePlausible } from '../hooks/usePlausible';

const SharePlaylistModal = inject('PlaylistStore')(
  observer(({ PlaylistStore }) => {
    const { sharingPlaylist, modalState, currentShares, mutualFriends } = PlaylistStore;
    const { trackEvent } = usePlausible();
    const [isLoading, setIsLoading] = useState(false);
    const [shareWithEveryone, setShareWithEveryone] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedPermission, setSelectedPermission] = useState('play');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleClose = () => {
      PlaylistStore.closeShareModal();
      setShareWithEveryone(false);
      setSelectedUserId('');
      setSelectedPermission('play');
      setDropdownOpen(false);
    };

    const handleAddShare = async () => {
      if (!shareWithEveryone && !selectedUserId) {
        showToast.error('Please select a friend to share with');
        return;
      }

      setIsLoading(true);
      try {
        const sharedWithUserId = shareWithEveryone ? null : parseInt(selectedUserId);
        await showToast.promise(
          PlaylistStore.addShare(sharingPlaylist.id, sharedWithUserId, selectedPermission),
          {
            loading: 'Adding share...',
            success: shareWithEveryone
              ? 'Playlist shared with all friends!'
              : 'Playlist shared successfully!',
            error: 'Failed to share playlist',
          }
        );

        // Track successful share
        trackEvent('Share Playlist', {
          playlistId: sharingPlaylist.id,
          shareType: shareWithEveryone ? 'everyone' : 'individual',
          permission: selectedPermission,
        });

        // Reload shares to get complete user data
        await PlaylistStore.loadPlaylistShares(sharingPlaylist.id);

        // Reset form
        setShareWithEveryone(false);
        setSelectedUserId('');
        setSelectedPermission('play');
        setDropdownOpen(false);
      } catch (error) {
        console.error('Failed to add share:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleUpdatePermission = async (shareId, newPermission) => {
      try {
        await showToast.promise(
          PlaylistStore.updateShare(sharingPlaylist.id, shareId, newPermission),
          {
            loading: 'Updating permission...',
            success: 'Permission updated!',
            error: 'Failed to update permission',
          }
        );

        // Track permission update
        trackEvent('Update Share Permission', {
          playlistId: sharingPlaylist.id,
          newPermission: newPermission,
        });
      } catch (error) {
        console.error('Failed to update permission:', error);
      }
    };

    const handleRemoveShare = async (shareId) => {
      try {
        await showToast.promise(
          PlaylistStore.removeShare(sharingPlaylist.id, shareId),
          {
            loading: 'Removing share...',
            success: 'Share removed!',
            error: 'Failed to remove share',
          }
        );

        // Track share removal
        trackEvent('Remove Share', {
          playlistId: sharingPlaylist.id,
        });
      } catch (error) {
        console.error('Failed to remove share:', error);
      }
    };

    return (
      <Modal
        isOpen={modalState.share}
        onClose={handleClose}
        title={`Share "${sharingPlaylist?.title}"`}
        size="lg"
      >
        <div className="space-y-6">
          {/* Add Share Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-text">
              Add Share
            </h3>

            {/* Share with everyone checkbox */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="share-everyone"
                checked={shareWithEveryone}
                onChange={(e) => {
                  setShareWithEveryone(e.target.checked);
                  if (e.target.checked) {
                    setSelectedUserId('');
                    setDropdownOpen(false);
                  }
                }}
                disabled={isLoading}
                className="
                  w-5 h-5 rounded border border-neutral-700 bg-transparent checked:bg-accent focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer
                "
              />
              <label
                htmlFor="share-everyone"
                className="text-sm text-text cursor-pointer"
              >
                Share with all mutual friends
              </label>
            </div>

            {/* Select specific friend */}
            {!shareWithEveryone && (
              <div className="relative">
                <label className="block text-sm font-medium text-text mb-2">
                  Select Friend
                </label>

                {/* Custom Dropdown Button */}
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  disabled={isLoading || mutualFriends.length === 0}
                  className="
                    w-full px-3 py-2 bg-transparent border border-neutral-700 focus:border-accent rounded-lg text-text transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2
                  "
                >
                  {selectedUserId ? (
                    <div className="flex items-center gap-2 flex-1">
                      <img
                        src={`${config.serverUrl}/users/profile/${mutualFriends.find(f => f.id.toString() === selectedUserId)?.username}/avatar`}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = logo;
                        }}
                      />
                      <span className="text-sm">
                        @{mutualFriends.find(f => f.id.toString() === selectedUserId)?.username}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm opacity-50">
                      {mutualFriends.length === 0 ? 'No friends available' : 'Select a friend...'}
                    </span>
                  )}
                  <svg className={`w-5 h-5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && mutualFriends.length > 0 && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setDropdownOpen(false)}
                    />

                    {/* Dropdown List */}
                    <div className="absolute z-20 w-full mt-1 max-h-48 overflow-y-auto bg-primary border border-neutral-700 rounded-lg shadow-lg">
                      {mutualFriends.map((friend) => (
                        <button
                          key={friend.id}
                          type="button"
                          onClick={() => {
                            setSelectedUserId(friend.id.toString());
                            setDropdownOpen(false);
                          }}
                          disabled={isLoading}
                          className={`
                            w-full flex items-center gap-3 p-3 hover:bg-secondary transition-colors border-b border-secondary last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed
                            ${selectedUserId === friend.id.toString() ? 'bg-accent bg-opacity-10 bg-opacity-10' : ''}
                          `}
                        >
                          {/* Avatar */}
                          <img
                            src={`${config.serverUrl}/users/profile/${friend.username}/avatar`}
                            alt={friend.username}
                            className="w-8 h-8 rounded-full object-cover border border-neutral-700"
                            onError={(e) => {
                              e.target.src = logo;
                            }}
                          />

                          {/* Username */}
                          <span className="text-sm font-medium text-text flex-1 text-left">
                            @{friend.username}
                          </span>

                          {/* Selected indicator */}
                          {selectedUserId === friend.id.toString() && (
                            <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Permission level */}
            <div>
              <label
                htmlFor="permission-level"
                className="block text-sm font-medium text-text mb-2"
              >
                Permission Level
              </label>
              <select
                id="permission-level"
                value={selectedPermission}
                onChange={(e) => setSelectedPermission(e.target.value)}
                disabled={isLoading}
                className="
                  w-full px-3 py-2 bg-transparent border border-neutral-700 focus:border-accent rounded-lg text-text transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <option value="play">View only (can play)</option>
                <option value="manage">Can manage (play + add/remove songs)</option>
              </select>
            </div>

            {/* Add Share Button */}
            <button
              onClick={handleAddShare}
              disabled={isLoading || (!shareWithEveryone && !selectedUserId)}
              className="
                w-full px-4 py-2 bg-accent text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium
              "
            >
              {isLoading ? 'Adding...' : 'Add Share'}
            </button>
          </div>

          {/* Current Shares List */}
          {currentShares.length > 0 && (
            <div className="border-t border-secondary pt-4">
              <h3 className="text-sm font-semibold text-text mb-4">
                Current Shares ({currentShares.length})
              </h3>

              <div className="space-y-3">
                {currentShares.map((share) => (
                  <div
                    key={share.id}
                    className="
                      flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary
                    "
                  >
                    {/* User info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {share.sharedWithUserId === null ? (
                        <>
                          {/* Everyone icon */}
                          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white text-lg flex-shrink-0">
                            🌐
                          </div>
                          <p className="text-sm font-medium text-text truncate">
                            Everyone (all mutual friends)
                          </p>
                        </>
                      ) : (
                        <>
                          {/* User avatar */}
                          <img
                            src={`${config.serverUrl}/users/profile/${share.SharedWith?.username || 'unknown'}/avatar`}
                            alt={share.SharedWith?.username || 'Unknown'}
                            className="w-10 h-10 rounded-full object-cover border-2 border-neutral-700 flex-shrink-0"
                            onError={(e) => {
                              e.target.src = logo;
                            }}
                          />
                          <p className="text-sm font-medium text-text truncate">
                            @{share.SharedWith?.username || 'Unknown'}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Permission dropdown */}
                    <select
                      value={share.permission}
                      onChange={(e) => handleUpdatePermission(share.id, e.target.value)}
                      className="
                        px-3 py-1.5 text-sm bg-transparent border border-secondary focus:border-accent rounded text-text transition-colors focus:outline-none
                      "
                    >
                      <option value="play">View only</option>
                      <option value="manage">Can manage</option>
                    </select>

                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveShare(share.id)}
                      className="
                        p-2 rounded text-red-500 hover:text-red-600 hover:bg-red-500 hover:bg-opacity-10 transition-all focus:outline-none focus:ring-2 focus:ring-red-500
                      "
                      aria-label="Remove share"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {currentShares.length === 0 && (
            <div className="text-center py-6 text-text opacity-50">
              <p className="text-sm">This playlist is not shared yet.</p>
            </div>
          )}
        </div>
      </Modal>
    );
  })
);

export default SharePlaylistModal;
